<script lang="ts" setup>
import { useSidePanelState } from "@/components/SidePanel/state.ts";

import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
} from "@headlessui/vue";
import { Bars3Icon, CheckIcon, PlusIcon, XMarkIcon } from "@heroicons/vue/24/outline";
import { UserCircleIcon } from "@heroicons/vue/24/solid";

import CreateTransaction from "@/components/EditTransaction/TransactionForm.vue";
import { logout } from "@/repositories/auth.ts";
import router from "@/router";
import EditBalance from "@/components/Balance/EditBalance.vue";
import { useAccount } from "@/stores/account.ts";
import { useEditTransaction } from "@/components/EditTransaction/state.ts";
import { themeOptions, useTheme } from "@/stores/theme.ts";

const sidePanel = useSidePanelState();
const theme = useTheme();
const account = useAccount();

const onCreateExpense = () => {
  const editTransactionState = useEditTransaction();

  sidePanel.open(CreateTransaction, {
    title: "Uitgave toevoegen",
    componentProps: { type: "expense" },
    onClose: () => editTransactionState.$reset(),
  });
};

const onCreateIncome = () => {
  const editTransactionState = useEditTransaction();

  sidePanel.open(CreateTransaction, {
    title: "Inkomen toevoegen",
    componentProps: { type: "income" },
    onClose: () => editTransactionState.$reset(),
  });
};

const onLogout = async () => {
  if (await logout()) {
    // Without this the profile stays set and App.vue keeps mounting the authenticated chrome.
    account.$reset();

    await router.push({ name: "login" });
  }
};
</script>

<template>
  <Disclosure v-slot="{ open }" as="nav" class="bg-white shadow">
    <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <div class="flex h-16 justify-between">
        <div class="flex">
          <div class="-ml-2 mr-2 flex items-center md:hidden">
            <!-- Mobile menu button -->
            <DisclosureButton
              class="relative inline-flex items-center justify-center rounded-md p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500"
            >
              <span class="absolute -inset-0.5" />
              <span class="sr-only">Open main menu</span>
              <Bars3Icon v-if="!open" aria-hidden="true" class="block size-6" />
              <XMarkIcon v-else aria-hidden="true" class="block size-6" />
            </DisclosureButton>
          </div>
          <div class="flex shrink-0 items-center">
            <img
              alt="Your Company"
              class="h-8 w-auto"
              src="https://tailwindcss.com/plus-assets/img/logos/mark.svg?color=indigo&shade=600"
            />
          </div>
          <div class="hidden md:ml-6 md:flex md:space-x-8">
            <!-- Current: "border-indigo-500 text-gray-900", Default: "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700" -->
            <a
              class="inline-flex items-center border-b-2 border-indigo-500 px-1 pt-1 text-sm font-medium text-gray-900"
              href="#"
              >Dashboard</a
            >
          </div>
        </div>
        <div class="flex items-center">
          <div class="flex flex-row space-x-2 items-center">
            <EditBalance></EditBalance>
            <div class="shrink-0">
              <button
                class="relative inline-flex items-center gap-x-1.5 rounded-md bg-green-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-500 focus-visible:outline focus-visible:outline-offset-2 focus-visible:outline-green-600"
                type="button"
                @click="onCreateIncome"
              >
                <PlusIcon aria-hidden="true" class="-ml-0.5 size-5" />
                Inkomen
              </button>
            </div>
            <div class="shrink-0">
              <button
                class="relative inline-flex items-center gap-x-1.5 rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 focus-visible:outline focus-visible:outline-offset-2 focus-visible:outline-red-600"
                type="button"
                @click="onCreateExpense"
              >
                <PlusIcon aria-hidden="true" class="-ml-0.5 size-5" />
                Uitgave
              </button>
            </div>
          </div>
          <div class="hidden md:ml-4 md:flex md:shrink-0 md:items-center">
            <Menu as="div" class="relative ml-3">
              <div>
                <MenuButton
                  class="cursor-pointer relative flex rounded-full bg-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-0.51"
                >
                  <span class="absolute -inset-1.5" />
                  <span class="sr-only">Open gebruiker menu</span>
                  <UserCircleIcon class="w-8 h-8 text-gray-400"></UserCircleIcon>
                </MenuButton>
              </div>
              <transition
                enter-active-class="transition ease-out duration-200"
                enter-from-class="transform opacity-0 scale-95"
                enter-to-class="transform opacity-100 scale-100"
                leave-active-class="transition ease-in duration-75"
                leave-from-class="transform opacity-100 scale-100"
                leave-to-class="transform opacity-0 scale-95"
              >
                <MenuItems
                  class="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5 focus:outline-none"
                >
                  <MenuItem v-slot="{ active }">
                    <a
                      :class="[
                        active ? 'bg-gray-100 outline-none' : '',
                        'block px-4 py-2 text-sm text-gray-700',
                      ]"
                      href="#"
                      >Profiel</a
                    >
                  </MenuItem>
                  <MenuItem v-slot="{ active }">
                    <a
                      :class="[
                        active ? 'bg-gray-100 outline-none' : '',
                        'block px-4 py-2 text-sm text-gray-700',
                      ]"
                      href="#"
                      >Instellingen</a
                    >
                  </MenuItem>
                  <div aria-label="Thema" class="border-t border-gray-200 py-1" role="group">
                    <p class="px-4 py-1 text-xs font-medium text-gray-500">Thema</p>
                    <template v-for="option in themeOptions" :key="option.value">
                      <MenuItem v-slot="{ active }">
                        <button
                          :aria-checked="theme.mode === option.value"
                          :class="[
                            active ? 'bg-gray-100 outline-none' : '',
                            'flex w-full cursor-pointer items-center justify-between px-4 py-2 text-sm text-gray-700',
                          ]"
                          role="menuitemradio"
                          type="button"
                          @click="theme.mode = option.value"
                        >
                          {{ option.label }}
                          <CheckIcon
                            v-if="theme.mode === option.value"
                            aria-hidden="true"
                            class="size-4 text-indigo-600"
                          />
                        </button>
                      </MenuItem>
                    </template>
                  </div>
                  <MenuItem v-slot="{ active }">
                    <button
                      :class="[
                        active ? 'bg-gray-100 outline-none' : '',
                        'block px-4 py-2 text-sm text-gray-700',
                      ]"
                      type="button"
                      @click="onLogout"
                    >
                      Uitloggen
                    </button>
                  </MenuItem>
                </MenuItems>
              </transition>
            </Menu>
          </div>
        </div>
      </div>
    </div>

    <DisclosurePanel class="md:hidden">
      <div class="space-y-1 pb-3 pt-2">
        <!-- Current: "bg-indigo-50 border-indigo-500 text-indigo-700", Default: "border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700" -->
        <DisclosureButton
          as="a"
          class="block border-l-4 border-indigo-500 bg-indigo-50 py-2 pl-3 pr-4 text-base font-medium text-indigo-700 sm:pl-5 sm:pr-6"
          href="#"
        >
          Dashboard
        </DisclosureButton>
      </div>
      <div class="border-t border-gray-200 pb-3 pt-4">
        <div class="flex items-center px-4 sm:px-6">
          <div class="shrink-0">
            <img
              alt=""
              class="size-10 rounded-full"
              src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80"
            />
          </div>
          <div class="ml-3">
            <div class="text-base font-medium text-gray-800">Tom Cook</div>
            <div class="text-sm font-medium text-gray-500">tom@example.com</div>
          </div>
        </div>
        <div class="mt-3 space-y-1">
          <DisclosureButton
            as="a"
            class="block px-4 py-2 text-base font-medium text-gray-500 hover:bg-gray-100 hover:text-gray-800 sm:px-6"
            href="#"
          >
            Your Profile
          </DisclosureButton>
          <DisclosureButton
            as="a"
            class="block px-4 py-2 text-base font-medium text-gray-500 hover:bg-gray-100 hover:text-gray-800 sm:px-6"
            href="#"
          >
            Settings
          </DisclosureButton>
          <DisclosureButton
            as="a"
            class="block px-4 py-2 text-base font-medium text-gray-500 hover:bg-gray-100 hover:text-gray-800 sm:px-6"
            href="#"
          >
            Sign out
          </DisclosureButton>
        </div>
      </div>

      <div class="border-t border-gray-200 px-4 pb-3 pt-4 sm:px-6">
        <p class="text-xs font-medium text-gray-500">Thema</p>
        <div aria-label="Thema" class="mt-2 flex gap-2" role="radiogroup">
          <button
            v-for="option in themeOptions"
            :key="option.value"
            :aria-checked="theme.mode === option.value"
            :class="[
              'flex-1 cursor-pointer rounded-md px-2.5 py-1.5 text-sm font-medium ring-1',
              theme.mode === option.value
                ? 'bg-indigo-600 text-white ring-indigo-600'
                : 'bg-white text-gray-900 ring-gray-300',
            ]"
            role="radio"
            type="button"
            @click="theme.mode = option.value"
          >
            {{ option.label }}
          </button>
        </div>
      </div>
    </DisclosurePanel>
  </Disclosure>
</template>

<style scoped></style>
